import { isIdleReserveMachine } from "../Machine/machineVisibility";
import { sameMachineIdent } from "./dashboardMapUtils";

export const MAX_PEOPLE_TRACE_PERIOD_MS = 31 * 24 * 60 * 60 * 1000;

export function peopleTracePeriodError(start, end) {
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "Informe o início e o fim.";
  }
  if (end.getTime() <= start.getTime()) {
    return "O fim deve ser posterior ao início.";
  }
  if (end.getTime() - start.getTime() > MAX_PEOPLE_TRACE_PERIOD_MS) {
    return "O período máximo é de 31 dias.";
  }
  return null;
}

export function dateInputToRange(startValue, endValue) {
  const startParts = String(startValue || "").split("-").map(Number);
  const endParts = String(endValue || "").split("-").map(Number);
  if (startParts.length !== 3 || endParts.length !== 3) return null;
  const start = new Date(startParts[0], startParts[1] - 1, startParts[2], 0, 0, 0, 0);
  const end = new Date(endParts[0], endParts[1] - 1, endParts[2], 23, 59, 59, 999);
  return { start, end };
}

/** null = todo mundo. Uma lista marca só quem fica no mapa. */
export function nextVisibleTrailIds(allIds, visibleIds, toggledId) {
  const all = (allIds || []).map(String);
  const current = Array.isArray(visibleIds) ? visibleIds.map(String) : all.slice();
  let next;
  if (toggledId === "*") {
    const allOn = all.length > 0 && all.every((id) => current.includes(id));
    next = allOn ? [] : all.slice();
  } else {
    const id = String(toggledId);
    next = current.includes(id)
      ? current.filter((item) => item !== id)
      : current.concat(id);
  }
  if (all.length > 0 && all.every((id) => next.includes(id))) return null;
  return next;
}

export function operatorShownOnMap(operatorId, visibleIds) {
  if (!Array.isArray(visibleIds)) return true;
  return visibleIds.some((id) => String(id) === String(operatorId));
}

export function machineTrailShown(machineIdent, operators, visibleIds) {
  if (!Array.isArray(visibleIds)) return true;
  const assumed = Object.values(operators || {}).find((operator) =>
    sameMachineIdent(
      operator && (operator.assumedMachineIdent || operator.iconMachineIdent),
      machineIdent
    )
  );
  if (!assumed) return true;
  return operatorShownOnMap(assumed.id, visibleIds);
}

export function machineIdentTrailShown(machineIdent, visibleIdents) {
  if (!Array.isArray(visibleIdents)) return true;
  return visibleIdents.some((ident) => sameMachineIdent(ident, machineIdent));
}

function trailOperatorName(operator) {
  return String((operator && operator.name) || "").trim();
}

/**
 * No painel, operador que já assumiu a máquina vira um rastro só.
 * Desmarcar a máquina não apaga esse rastro: ele continua como o do operador.
 */
export function peopleTrailDrawList({
  operators,
  machines,
  visibleOperatorIds,
  visibleMachineIdents,
}) {
  const people = Object.values(operators || {});
  const fleet = Object.values(machines || {});
  const draws = [];
  const coveredMachineIds = new Set();

  people.forEach((operator) => {
    if (!operator || !operatorShownOnMap(operator.id, visibleOperatorIds)) return;
    const machine = (operator.assumedMachineIdent
      ? fleet.find((item) => sameMachineIdent(item && item.ident, operator.assumedMachineIdent))
      : null) || null;
    if (machine) {
      coveredMachineIds.add(String(machine.id));
      draws.push({
        source: "pair",
        operatorId: operator.id,
        machineId: machine.id,
        subject: {
          kind: "pair",
          name: trailOperatorName(operator),
          operatorName: trailOperatorName(operator),
          operatorId: operator.id,
          machineIdent: machine.ident,
        },
      });
      return;
    }
    draws.push({
      source: "operator",
      operatorId: operator.id,
      subject: {
        kind: "operator",
        name: trailOperatorName(operator),
        operatorName: trailOperatorName(operator),
        operatorId: operator.id,
        machineIdent: "",
      },
    });
  });

  fleet.forEach((machine) => {
    if (!machine || !machineIdentTrailShown(machine.ident, visibleMachineIdents)) return;
    if (coveredMachineIds.has(String(machine.id))) return;
    const assumed = people.find((operator) =>
      sameMachineIdent(operator && operator.assumedMachineIdent, machine.ident)
    );
    draws.push({
      source: "machine",
      machineId: machine.id,
      subject: {
        kind: "machine",
        name: machine.ident,
        machineIdent: machine.ident,
        operatorId: assumed ? assumed.id : null,
        operatorName: assumed ? trailOperatorName(assumed) : "",
      },
    });
  });

  return draws;
}

/** null no grupo = cadastro inteiro. null nos rastros = todos do grupo. */
export function trailsToDraw(rosterIds, visibleIds) {
  if (Array.isArray(rosterIds)) {
    const roster = rosterIds.map(String);
    if (!Array.isArray(visibleIds)) return roster;
    const allowed = new Set(roster);
    return visibleIds.map(String).filter((id) => allowed.has(id));
  }
  if (!Array.isArray(visibleIds)) return null;
  return visibleIds.map(String);
}

export function readTrailRoster(farmId) {
  const empty = { operatorIds: null, machineIdents: null };
  if (!farmId) return empty;
  try {
    const raw = localStorage.getItem(`dashboard-trail-roster-${farmId}`);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    return {
      operatorIds: Array.isArray(parsed.operatorIds)
        ? parsed.operatorIds.map(String)
        : null,
      machineIdents: Array.isArray(parsed.machineIdents)
        ? parsed.machineIdents.map(String)
        : null,
    };
  } catch (error) {
    return empty;
  }
}

export function writeTrailRoster(farmId, roster) {
  if (!farmId) return;
  const next = {
    operatorIds: Array.isArray(roster && roster.operatorIds)
      ? roster.operatorIds.map(String)
      : null,
    machineIdents: Array.isArray(roster && roster.machineIdents)
      ? roster.machineIdents.map(String)
      : null,
  };
  localStorage.setItem(`dashboard-trail-roster-${farmId}`, JSON.stringify(next));
}

function personName(person) {
  if (!person) return "";
  return String(person.nome || person.name || person.ident || "").trim();
}

export function catalogPeople(catalog, mapOperators) {
  const byId = new Map();
  (catalog || []).forEach((person) => {
    const name = personName(person);
    if (!person || person.id == null || !name) return;
    byId.set(String(person.id), { id: person.id, name });
  });
  Object.values(mapOperators || {}).forEach((person) => {
    const name = personName(person);
    if (!person || person.id == null || !name || byId.has(String(person.id))) return;
    byId.set(String(person.id), { id: person.id, name });
  });
  return Array.from(byId.values()).sort((left, right) =>
    left.name.localeCompare(right.name, "pt-BR")
  );
}

export function peopleInRoster(people, rosterIds) {
  if (!Array.isArray(rosterIds)) return people || [];
  const allowed = new Set(rosterIds.map(String));
  return (people || []).filter((person) => allowed.has(String(person.id)));
}

export function catalogMachines(catalog, equipment) {
  const byIdent = new Map();
  const add = (machine) => {
    if (isIdleReserveMachine(machine)) return;
    const ident = String((machine && machine.ident) || "").trim();
    if (!ident) return;
    const key = ident.toLowerCase();
    if (!byIdent.has(key)) byIdent.set(key, { ident });
  };
  (catalog || []).forEach(add);
  Object.values(equipment || {}).forEach(add);
  return Array.from(byIdent.values()).sort((left, right) =>
    left.ident.localeCompare(right.ident, "pt-BR")
  );
}

export function recentTrailDays(now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = [];
  for (let offset = 0; offset <= 6; offset += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - offset);
    days.push(date);
  }
  return days;
}

export function trailDayKey(date) {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${value.getFullYear()}-${month}-${day}`;
}

export function isTrailHourKey(key) {
  return key === "1h" || key === "12h";
}

export function trailHourKey(hours) {
  const value = Number(hours);
  if (value === 1) return "1h";
  if (value === 12) return "12h";
  return "";
}

export function toggleTrailPeriodKey(keys, key) {
  const current = Array.isArray(keys) ? keys.filter(Boolean) : [];
  if (!key) return current;
  return current.includes(key)
    ? current.filter((item) => item !== key)
    : current.concat(key);
}

export function toggleTrailDayKeys(keys, date) {
  return toggleTrailPeriodKey(keys, trailDayKey(date));
}

function hoursRange(hours, now = new Date()) {
  const end = new Date(now);
  const start = new Date(end.getTime() - Number(hours) * 60 * 60 * 1000);
  return { start, end };
}

function stampInRange(stampMs, range) {
  if (!range || !Number.isFinite(stampMs)) return false;
  return stampMs >= range.start.getTime() && stampMs <= range.end.getTime() + 60 * 1000;
}

/** União de 1h, 12h e dias de calendário marcados. */
export function periodForTrailDayKeys(keys, now = new Date()) {
  const ranges = [];
  (keys || []).forEach((key) => {
    if (key === "1h") {
      ranges.push(hoursRange(1, now));
      return;
    }
    if (key === "12h") {
      ranges.push(hoursRange(12, now));
      return;
    }
    const parts = String(key).split("-").map(Number);
    if (parts.length !== 3 || parts.some((part) => !part)) return;
    ranges.push(calendarDayRange(new Date(parts[0], parts[1] - 1, parts[2])));
  });
  if (!ranges.length) return null;
  return {
    start: new Date(Math.min(...ranges.map((range) => range.start.getTime()))),
    end: new Date(Math.max(...ranges.map((range) => range.end.getTime()))),
  };
}

export function stampOnTrailDays(stamp, keys, now = new Date()) {
  if (!Array.isArray(keys) || keys.length === 0) return true;
  const stampMs =
    stamp instanceof Date ? stamp.getTime() : new Date(stamp).getTime();
  if (!Number.isFinite(stampMs)) return false;
  if (keys.includes("1h") && stampInRange(stampMs, hoursRange(1, now))) {
    return true;
  }
  if (keys.includes("12h") && stampInRange(stampMs, hoursRange(12, now))) {
    return true;
  }
  const dayKeys = keys.filter((key) => !isTrailHourKey(key));
  if (!dayKeys.length) return false;
  const key = trailDayKey(stamp);
  return Boolean(key) && dayKeys.includes(key);
}

export function calendarDayRange(date) {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  const end = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  return { start, end };
}

function sameClock(left, right) {
  return (
    left instanceof Date &&
    right instanceof Date &&
    Math.abs(left.getTime() - right.getTime()) < 1000
  );
}

/** 1h e 12h são janelas. O dia de hoje vale as últimas 24 h. */
export function activeTrailChip(start, end, now = new Date()) {
  if (!(start instanceof Date) || !(end instanceof Date)) return "";
  const days = recentTrailDays(now);
  for (let index = 0; index < days.length; index += 1) {
    const range = calendarDayRange(days[index]);
    if (sameClock(start, range.start) && sameClock(end, range.end)) {
      return `${days[index].getFullYear()}-${String(days[index].getMonth() + 1).padStart(2, "0")}-${String(days[index].getDate()).padStart(2, "0")}`;
    }
  }
  const span = end.getTime() - start.getTime();
  const hour = 60 * 60 * 1000;
  if (Math.abs(span - hour) < 60 * 1000) return "1h";
  if (Math.abs(span - 12 * hour) < 60 * 1000) return "12h";
  return "custom";
}

export function machinesInRoster(machines, rosterIdents) {
  if (!Array.isArray(rosterIdents)) return machines || [];
  return (machines || []).filter((machine) =>
    rosterIdents.some((ident) => sameMachineIdent(ident, machine.ident))
  );
}
